import { useCallback, useEffect, useRef, useState } from "react"

import { NodeViewWrapper } from "@tiptap/react"
import { createPortal } from "react-dom"

import type { NodeViewProps } from "@tiptap/react"

import { getCitationFilePath } from "@/api/api"
import { CitationIcon } from "@/features/chat/components/citation-icon"
import { CitationPeekContent } from "@/features/chat/components/citation-peek-content"
import { CitationSidebarHeader } from "@/features/chat/components/citation-sidebar-header"
import { CitationViewerContent } from "@/features/chat/components/citation-viewer-content"
import { getCitationType } from "@/features/chat/components/citation.utils"
import i18n from "@/languageConfig"
import { useContentSidebarStore } from "@/stores/content-sidebar-store"
import { parseCitationParts, resolveCitationDisplayTitle } from "@/utils/citation-utils"
import { normalizeForMatching } from "@/utils/normalize-for-matching"

import { useCanvasStore } from "../stores/canvas-store"

const HOVER_OPEN_DELAY_MS = 400
const HOVER_CLOSE_DELAY_MS = 150
const PEEK_CARD_WIDTH_PX = 320
const EDGE_MARGIN_PX = 12

type HoverAnchor = {
  anchorRect: DOMRect
  placement: "below" | "above"
}

function getPlacement(anchorRect: DOMRect): "below" | "above" {
  const availableBelow = window.innerHeight - anchorRect.bottom - EDGE_MARGIN_PX
  const availableAbove = anchorRect.top - EDGE_MARGIN_PX
  return availableBelow >= availableAbove ? "below" : "above"
}

function getPeekCardStyle(anchorRect: DOMRect, placement: "below" | "above"): React.CSSProperties {
  const maxLeft = Math.max(EDGE_MARGIN_PX, window.innerWidth - PEEK_CARD_WIDTH_PX - EDGE_MARGIN_PX)
  const left = Math.min(Math.max(anchorRect.left, EDGE_MARGIN_PX), maxLeft)

  if (placement === "below") {
    return {
      position: "fixed",
      top: anchorRect.bottom + 8,
      left,
      width: PEEK_CARD_WIDTH_PX,
      zIndex: 60,
    }
  }
  return {
    position: "fixed",
    bottom: window.innerHeight - anchorRect.top + 8,
    left,
    width: PEEK_CARD_WIDTH_PX,
    zIndex: 60,
  }
}

export function CitationNodeView({ node }: NodeViewProps) {
  const citation = (node.attrs.citation as string) ?? "" // eslint-disable-line @typescript-eslint/no-unnecessary-condition
  const index = (node.attrs.index as number) ?? 0 // eslint-disable-line @typescript-eslint/no-unnecessary-condition
  const citationType = getCitationType(citation)

  const sessionId = useCanvasStore((state) => state.sessionId)

  const buttonRef = useRef<HTMLButtonElement | null>(null)
  const [hoverAnchor, setHoverAnchor] = useState<HoverAnchor | null>(null)
  const openTimerRef = useRef<number | null>(null)
  const closeTimerRef = useRef<number | null>(null)

  const openFullPreview = useCallback(() => {
    if (citationType !== "library") return

    const parsed = parseCitationParts(citation)
    // Backend ignores the section segment for library citations; it only needs to be non-empty.
    // Fall back to the library ID so the URL never contains a double-slash.
    const libraryIdFallback = parsed.source.split(":")[0] || "library"
    const citationSection = sessionId ?? libraryIdFallback
    const documentUrl =
      getCitationFilePath(parsed.source, citationSection) +
      (parsed.page != null ? `#page=${parsed.page}` : "")
    const citationTitle = resolveCitationDisplayTitle(citation)
    const quoteSnippet = parsed.quote?.trim() ? normalizeForMatching(parsed.quote) : ""

    useContentSidebarStore.getState().pushPanel({
      id: "citation-preview",
      title: citationTitle || i18n.t("analysispanel.citation", { defaultValue: "Cited document" }),
      headerMode: "custom",
      renderHeader: (context) => (
        <CitationSidebarHeader
          closeable={context.closeable}
          downloadUrl={documentUrl}
          isFocusable={context.isFocusable}
          isFocused={context.isFocused}
          title={citationTitle}
          onBack={() => useContentSidebarStore.getState().popPanel()}
          onClose={context.onClose}
          onToggleFocus={context.onToggleFocus}
        />
      ),
      content: (
        <CitationViewerContent
          chunkSnippet=""
          documentUrl={documentUrl}
          quoteSnippet={quoteSnippet}
        />
      ),
      focusable: true,
      closeable: true,
      resizable: true,
      isDirty: () => false,
    })
  }, [citation, citationType, sessionId])

  const handleClick = useCallback(() => {
    setHoverAnchor(null)
    if (citationType === "web") {
      const url = parseCitationParts(citation).source
      window.open(url, "_blank", "noopener,noreferrer")
      return
    }
    openFullPreview()
  }, [citation, citationType, openFullPreview])

  const clearOpenTimer = useCallback(() => {
    if (openTimerRef.current !== null) {
      window.clearTimeout(openTimerRef.current)
      openTimerRef.current = null
    }
  }, [])

  const clearCloseTimer = useCallback(() => {
    if (closeTimerRef.current !== null) {
      window.clearTimeout(closeTimerRef.current)
      closeTimerRef.current = null
    }
  }, [])

  const handleMouseEnter = useCallback(() => {
    clearCloseTimer()
    if (citationType !== "library") return

    clearOpenTimer()
    openTimerRef.current = window.setTimeout(() => {
      const button = buttonRef.current
      if (!button) return
      const anchorRect = button.getBoundingClientRect()
      setHoverAnchor({ anchorRect, placement: getPlacement(anchorRect) })
    }, HOVER_OPEN_DELAY_MS)
  }, [citationType, clearCloseTimer, clearOpenTimer])

  const handleMouseLeave = useCallback(() => {
    clearOpenTimer()
    clearCloseTimer()
    closeTimerRef.current = window.setTimeout(() => {
      setHoverAnchor(null)
    }, HOVER_CLOSE_DELAY_MS)
  }, [clearCloseTimer, clearOpenTimer])

  useEffect(() => {
    return () => {
      clearOpenTimer()
      clearCloseTimer()
    }
  }, [clearCloseTimer, clearOpenTimer])

  const parsed = parseCitationParts(citation)
  const displayTitle = resolveCitationDisplayTitle(citation)

  return (
    <NodeViewWrapper as="span" className="inline">
      <button
        ref={buttonRef}
        className="supContainer !mt-1"
        contentEditable={false}
        type="button"
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <sup className="inline-flex items-center justify-center gap-1 px-[0.3125rem] py-0.5">
          <span className="mt-px inline-block leading-none">{index + 1}</span>
          <span className="inline-flex items-center">
            <CitationIcon citation={citation} className="size-3" type={citationType} />
          </span>
        </sup>
      </button>
      {hoverAnchor &&
        citationType === "library" &&
        createPortal(
          <div
            className="bg-popover text-popover-foreground rounded-md border p-4 shadow-md"
            style={getPeekCardStyle(hoverAnchor.anchorRect, hoverAnchor.placement)}
            onMouseEnter={clearCloseTimer}
            onMouseLeave={handleMouseLeave}
          >
            <CitationPeekContent
              hidePreviewText={true}
              interactionMode="button"
              pageNumber={parsed.page}
              title={displayTitle}
              onViewDocument={() => {
                setHoverAnchor(null)
                openFullPreview()
              }}
            />
          </div>,
          document.body
        )}
    </NodeViewWrapper>
  )
}
